// Shared Action Register table + kanban (W3/W4). KPI counts always derived from rows.
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useRowFocus } from "../../agent/useAgentFocus";
import { brainTokens } from "../../design/brainTheme";
import { ActionRegisterTable, EmptyState, SectionCard } from "../../mui";
import {
  ACTION_CATEGORY_LABELS,
  RAISE_TARGET_LABELS,
  createProjectAction,
  escalateActionToJira,
  listActionHistory,
  updateProjectAction,
  type ActionCategory,
  type ActionHistoryEntry,
  type ProjectAction,
} from "../../lib/brain-api";
import { CLIENT_STATUS_LANES, groupActions, type ActionGroupBy } from "./actionGrouping";

export { CLIENT_STATUS_LANES };

const c = brainTokens.color;

type RaiseTargetKey = "dev" | "support" | "feature-request";

// Mirrors server RAISE_TARGETS (constants/raise-config.ts) so the picker says
// where the issue will land.
const RAISE_TARGET_OPTIONS: { key: RaiseTargetKey; label: string }[] = [
  { key: "dev", label: "Dev (ANX · Task)" },
  { key: "support", label: "Support (TICKET · Task)" },
  { key: "feature-request", label: "Feature request (TICKET · Task)" },
];

export function actionGlyphs(a: ProjectAction): string {
  const g: string[] = [];
  if (a.priority === "P1") g.push("⭐");
  if (a.isGate) g.push("🔑");
  if (a.parentActionId) g.push("↳");
  if (a.owedBy === "aeronet") g.push("❓");
  if (a.status === "proposed" || a.status === "suggested") g.push("🆕");
  if (a.owedBy === "customer" && (a.status === "done" || a.clientStatus === "Done")) g.push("✔D");
  if (a.status === "dismissed") g.push("D");
  // Verified is orthogonal to status: done = finished, verified = someone signed it off.
  if (a.verifiedBy) g.push("✓");
  return g.join("");
}

const HISTORY_FIELD_LABELS: Record<string, string> = {
  status: "Status",
  clientStatus: "Client status",
  assignee: "Owner",
  priority: "Priority",
  owedBy: "Owed by",
  workStream: "Workstream",
  isGate: "Gate",
  verifiedBy: "Verified by",
  jiraIssueKey: "Ticket",
  dueDate: "Due",
};

function whenLabel(ts: number): string {
  const mins = Math.round((Date.now() - ts) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
  return new Date(ts).toLocaleDateString("en-NZ");
}

/** Consolidation label: one Jira key with #n folded points. */
export function ticketLabel(a: ProjectAction, all: ProjectAction[]): string {
  const key = a.jiraIssueKey;
  if (!key) return "—";
  const n = all.filter((x) => x.jiraIssueKey === key).length;
  return n > 1 ? `[${key} #${n}]` : key;
}

export function ProjectActionsTab({
  projectId,
  workStreams = [],
  initialActions,
  showProjectCol = false,
  nzDate = "",
  defaultReleaseVersion,
  onChanged,
}: {
  projectId?: string;
  workStreams?: string[];
  initialActions: ProjectAction[];
  showProjectCol?: boolean;
  nzDate?: string;
  /** Optional project-level fixVersion stamped onto quick-adds. */
  defaultReleaseVersion?: string;
  onChanged?: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  // Agent/deep-link arrival: ?action=<id> or a [ref:action:…] chip flashes the row.
  const { flashId, scrollRef } = useRowFocus("action", "action");
  const [actions, setActions] = useState(initialActions);
  const [view, setView] = useState<"list" | "kanban">("list");
  const [groupBy, setGroupBy] = useState<ActionGroupBy>("clientStatus");
  const [dragId, setDragId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState<Partial<ProjectAction> & { text: string } | null>(null);
  const [raiseFor, setRaiseFor] = useState<{ action: ProjectAction; target: RaiseTargetKey } | null>(null);
  const [history, setHistory] = useState<ActionHistoryEntry[] | null>(null);

  useEffect(() => {
    setActions(initialActions);
  }, [initialActions]);

  // History is fetched lazily when the edit dialog opens, not with the action list.
  const editId = edit?.id;
  useEffect(() => {
    if (!editId) { setHistory(null); return; }
    let live = true;
    setHistory(null);
    listActionHistory(editId)
      .then((h) => { if (live) setHistory(h); })
      .catch(() => { if (live) setHistory([]); });
    return () => { live = false; };
  }, [editId]);

  const openCount = actions.filter((a) => a.status !== "done" && a.status !== "dismissed").length;
  const gateCount = actions.filter((a) => a.isGate && a.status !== "done" && a.status !== "dismissed").length;

  // Empty select/field means "clear" — the PATCH needs an explicit null, since
  // JSON.stringify drops undefined keys and the clear would silently no-op.
  const orNull = <T extends string>(v: T | undefined | ""): T | null =>
    v !== undefined && v !== "" ? v : null;

  const save = async () => {
    if (!edit?.text?.trim()) return;
    setBusy(true);
    try {
      if (edit.id) {
        const updated = await updateProjectAction(edit.id, {
          text: edit.text,
          status: edit.status,
          workStream: orNull(edit.workStream),
          priority: orNull(edit.priority),
          owedBy: orNull(edit.owedBy),
          clientStatus: orNull(edit.clientStatus as string | undefined),
          contact: orNull(edit.contact?.trim()),
          assignee: orNull(edit.assignee?.trim()),
          isGate: edit.isGate ?? false,
          customerRef: orNull(edit.customerRef?.trim()),
          chargeable: edit.chargeable ?? false,
          category: orNull(edit.category),
          needsTicket: edit.needsTicket ?? false,
          raiseTarget: edit.needsTicket ? orNull(edit.raiseTarget) : null,
          comments: orNull(edit.comments?.trim()),
        });
        setActions((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
      } else {
        // Strip empty-string fields so a blank dialog input isn't stored as "".
        const clean = Object.fromEntries(
          Object.entries(edit).filter(([, v]) => v !== "" && v !== undefined),
        ) as Partial<ProjectAction>;
        const created = await createProjectAction({
          ...clean,
          projectId: projectId ?? edit.projectId,
          kind: "action",
          text: edit.text.trim(),
        });
        setActions((prev) => [created, ...prev]);
      }
      setEdit(null);
      onChanged?.();
      enqueueSnackbar("Action saved", { variant: "success" });
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : String(e), { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  /** Optimistic single-field patch — used by kanban drops and the Verify button. */
  const patchRow = async (
    a: ProjectAction,
    patch: Parameters<typeof updateProjectAction>[1],
    optimistic: Partial<ProjectAction>,
  ) => {
    setActions((prev) => prev.map((x) => (x.id === a.id ? { ...x, ...optimistic } : x)));
    try {
      const updated = await updateProjectAction(a.id, patch);
      setActions((prev) => prev.map((x) => (x.id === a.id ? updated : x)));
      if (edit?.id === a.id) setEdit({ ...updated, text: updated.text });
      onChanged?.();
      return updated;
    } catch (e) {
      setActions((prev) => prev.map((x) => (x.id === a.id ? a : x))); // revert
      enqueueSnackbar(e instanceof Error ? e.message : String(e), { variant: "error" });
      return null;
    }
  };

  const dropOnLane = async (laneKey: string | null) => {
    const a = actions.find((x) => x.id === dragId);
    setDragId(null);
    if (!a) return;
    // Send ONLY the field the lane represents; null clears it (Unset / Unassigned).
    if (groupBy === "assignee") {
      if ((a.assignee ?? null) === laneKey) return;
      await patchRow(a, { assignee: laneKey }, { assignee: laneKey ?? undefined });
    } else {
      if ((a.clientStatus ?? null) === laneKey) return;
      await patchRow(a, { clientStatus: laneKey }, { clientStatus: (laneKey ?? undefined) as ProjectAction["clientStatus"] });
    }
  };

  const raise = async (a: ProjectAction, target: RaiseTargetKey) => {
    if (a.jiraIssueKey) return;
    setBusy(true);
    try {
      const out = await escalateActionToJira(a.id, { target, summary: a.text });
      if (out.action) {
        setActions((prev) => prev.map((x) => (x.id === a.id ? out.action! : x)));
      } else if (out.key) {
        setActions((prev) => prev.map((x) => (x.id === a.id ? { ...x, jiraIssueKey: out.key } : x)));
      }
      enqueueSnackbar(`Raised ${out.key}`, { variant: "success" });
      onChanged?.();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : String(e), { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", gap: 2, fontSize: 13, color: c.muted2 }}>
          <Box>
            Open{" "}
            <Box component="span" sx={{ fontWeight: 700, color: c.ink }}>
              {openCount}
            </Box>
          </Box>
          <Box>
            Gates{" "}
            <Box component="span" sx={{ fontWeight: 700, color: c.ink }}>
              {gateCount}
            </Box>
          </Box>
          <Box>
            Total{" "}
            <Box component="span" sx={{ fontWeight: 700, color: c.ink }}>
              {actions.length}
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={view}
            onChange={(_, v) => v && setView(v)}
          >
            <ToggleButton value="list">List</ToggleButton>
            <ToggleButton value="kanban">Kanban</ToggleButton>
          </ToggleButtonGroup>
          {view === "kanban" && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={groupBy}
              onChange={(_, v) => v && setGroupBy(v as ActionGroupBy)}
            >
              <ToggleButton value="clientStatus">By status</ToggleButton>
              <ToggleButton value="assignee">By owner</ToggleButton>
            </ToggleButtonGroup>
          )}
          {projectId && (
            <Button
              variant="contained"
              size="small"
              disabled={busy}
              onClick={() =>
                setEdit({
                  text: "",
                  projectId,
                  status: "proposed",
                  kind: "action",
                  clientStatus: "With Aeronet",
                })
              }
            >
              Add action
            </Button>
          )}
        </Box>
      </Box>

      {view === "list" ? (
        actions.length === 0 && !projectId ? (
          <EmptyState icon="list" title="No actions yet">
            Add an engagement row to start the register.
          </EmptyState>
        ) : (
          <Box
            ref={flashId ? scrollRef : undefined}
            className={flashId ? "agent-focus-flash" : undefined}
          >
            <ActionRegisterTable
              projectId={projectId}
              actions={actions}
              workStreams={workStreams}
              defaultGroupsOpen={false}
              nzDate={nzDate}
              canQuickAdd={!!projectId}
              canEdit
              showProjectCol={showProjectCol}
              defaultReleaseVersion={defaultReleaseVersion}
              blockedStreams={actions
                .filter((a) => a.kind === "blocker" && a.status !== "done" && a.status !== "dismissed" && a.workStream)
                .map((a) => a.workStream!)}
              onActionsChange={(next) => {
                setActions(next);
                // Don't bounce to parent until rows are persisted (optimistic ids are temporary).
                if (next.every((a) => !a.id.startsWith("optimistic-"))) onChanged?.();
              }}
              onRowClick={(a) => setEdit({ ...a, text: a.text })}
            />
          </Box>
        )
      ) : actions.length === 0 ? (
        <EmptyState icon="list" title="No actions yet">
          Add an engagement row to start the register.
        </EmptyState>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)", lg: "repeat(4, 1fr)" },
            gap: "12px",
            alignItems: "start",
          }}
        >
          {groupActions(actions, groupBy).map((lane) => (
            <Box
              key={lane.label}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); void dropOnLane(lane.key); }}
            >
              <SectionCard label={`${lane.label} · ${lane.rows.length}`}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minHeight: 40 }}>
                  {lane.rows.length === 0 ? (
                    <Box sx={{ fontSize: 12, color: c.muted2 }}>Empty</Box>
                  ) : (
                    lane.rows.map((a) => (
                      <Box
                        key={a.id}
                        draggable
                        onDragStart={() => setDragId(a.id)}
                        onDragEnd={() => setDragId(null)}
                        sx={{
                          p: 1.25,
                          borderRadius: "8px",
                          border: `1px solid ${c.border}`,
                          background: c.pageBg,
                          cursor: "grab",
                          opacity: dragId === a.id ? 0.4 : 1,
                        }}
                        onClick={() => setEdit({ ...a, text: a.text })}
                      >
                        <Box sx={{ fontSize: 12, color: c.muted2, mb: 0.5 }}>
                          {actionGlyphs(a)} {a.customerRef ?? ""}
                        </Box>
                        <Box sx={{ fontSize: 13, color: c.ink, lineHeight: 1.4 }}>{a.text}</Box>
                        <Box sx={{ fontSize: 11, color: c.muted2, mt: 0.75, fontFamily: brainTokens.font.mono }}>
                          {ticketLabel(a, actions)}
                        </Box>
                      </Box>
                    ))
                  )}
                </Box>
              </SectionCard>
            </Box>
          ))}
        </Box>
      )}

      <Dialog open={!!raiseFor} onClose={() => setRaiseFor(null)} fullWidth maxWidth="xs">
        <DialogTitle>Raise a Jira issue</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "12px !important" }}>
          <Box sx={{ fontSize: 13, color: c.muted2 }}>{raiseFor?.action.text}</Box>
          <TextField
            label="Target"
            select
            fullWidth
            size="small"
            value={raiseFor?.target ?? "dev"}
            onChange={(e) =>
              setRaiseFor((prev) => (prev ? { ...prev, target: e.target.value as RaiseTargetKey } : prev))
            }
          >
            {RAISE_TARGET_OPTIONS.map((o) => (
              <MenuItem key={o.key} value={o.key}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRaiseFor(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={busy}
            onClick={() => {
              if (!raiseFor) return;
              const { action, target } = raiseFor;
              setRaiseFor(null);
              void raise(action, target);
            }}
          >
            Raise
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!edit} onClose={() => setEdit(null)} fullWidth maxWidth="sm">
        <DialogTitle>{edit?.id ? "Edit action" : "Add action"}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "12px !important" }}>
          <TextField
            label="Item"
            fullWidth
            size="small"
            value={edit?.text ?? ""}
            onChange={(e) => setEdit((prev) => (prev ? { ...prev, text: e.target.value } : prev))}
          />
          <TextField
            label="Ref"
            fullWidth
            size="small"
            value={edit?.customerRef ?? ""}
            onChange={(e) => setEdit((prev) => (prev ? { ...prev, customerRef: e.target.value } : prev))}
          />
          <TextField
            label="Workstream"
            select
            fullWidth
            size="small"
            value={edit?.workStream ?? ""}
            onChange={(e) => setEdit((prev) => (prev ? { ...prev, workStream: e.target.value || undefined } : prev))}
          >
            <MenuItem value="">(ungrouped)</MenuItem>
            {workStreams.map((ws) => (
              <MenuItem key={ws} value={ws}>
                {ws}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Type"
            select
            fullWidth
            size="small"
            value={edit?.category ?? ""}
            helperText="Call-report classification — FR needs dev + a Buy-or-Wait quote"
            onChange={(e) =>
              setEdit((prev) => (prev ? { ...prev, category: (e.target.value || undefined) as ActionCategory | undefined } : prev))
            }
          >
            <MenuItem value="">—</MenuItem>
            {(Object.keys(ACTION_CATEGORY_LABELS) as ActionCategory[]).map((k) => (
              <MenuItem key={k} value={k}>
                {ACTION_CATEGORY_LABELS[k]}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Client status"
            select
            fullWidth
            size="small"
            value={edit?.clientStatus ?? ""}
            onChange={(e) => setEdit((prev) => (prev ? { ...prev, clientStatus: e.target.value } : prev))}
          >
            {CLIENT_STATUS_LANES.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Priority"
            select
            fullWidth
            size="small"
            value={edit?.priority ?? ""}
            onChange={(e) => setEdit((prev) => (prev ? { ...prev, priority: e.target.value || undefined } : prev))}
          >
            <MenuItem value="">—</MenuItem>
            {["P1", "P2", "P3"].map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Owed by"
            select
            fullWidth
            size="small"
            value={edit?.owedBy ?? ""}
            onChange={(e) =>
              setEdit((prev) =>
                prev ? { ...prev, owedBy: (e.target.value || undefined) as "aeronet" | "customer" | undefined } : prev,
              )
            }
          >
            <MenuItem value="">—</MenuItem>
            <MenuItem value="aeronet">aeronet</MenuItem>
            <MenuItem value="customer">customer</MenuItem>
          </TextField>
          <TextField
            label="Contact"
            fullWidth
            size="small"
            value={edit?.contact ?? ""}
            onChange={(e) => setEdit((prev) => (prev ? { ...prev, contact: e.target.value } : prev))}
          />
          <TextField
            label="Owner"
            fullWidth
            size="small"
            value={edit?.assignee ?? ""}
            onChange={(e) => setEdit((prev) => (prev ? { ...prev, assignee: e.target.value } : prev))}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Checkbox
              checked={!!edit?.isGate}
              onChange={(e) => setEdit((prev) => (prev ? { ...prev, isGate: e.target.checked } : prev))}
            />
            <Box sx={{ fontSize: 13 }}>Gate 🔑</Box>
            <Checkbox
              checked={!!edit?.chargeable}
              onChange={(e) => setEdit((prev) => (prev ? { ...prev, chargeable: e.target.checked } : prev))}
            />
            <Box sx={{ fontSize: 13 }}>Chargeable</Box>
            <Checkbox
              checked={!!edit?.needsTicket}
              onChange={(e) => setEdit((prev) => (prev ? { ...prev, needsTicket: e.target.checked } : prev))}
            />
            <Box sx={{ fontSize: 13 }}>Needs a ticket</Box>
          </Box>
          {edit?.needsTicket && (
            <TextField
              label="Raise on"
              select
              fullWidth
              size="small"
              value={edit?.raiseTarget ?? ""}
              helperText="Flags where it should be raised — does NOT create the issue (use Raise Jira for that)"
              onChange={(e) => setEdit((prev) => (prev ? { ...prev, raiseTarget: e.target.value || undefined } : prev))}
            >
              <MenuItem value="">—</MenuItem>
              {Object.keys(RAISE_TARGET_LABELS).map((k) => (
                <MenuItem key={k} value={k}>
                  {RAISE_TARGET_LABELS[k]}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            label="Comments"
            fullWidth
            multiline
            minRows={2}
            size="small"
            value={edit?.comments ?? ""}
            onChange={(e) => setEdit((prev) => (prev ? { ...prev, comments: e.target.value } : prev))}
          />

          {edit?.id && (
            <Box component="details" sx={{ fontSize: 12, color: c.muted2 }}>
              <Box component="summary" sx={{ cursor: "pointer", py: 0.5 }}>History</Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, pt: 1 }}>
                {history === null ? (
                  <Box>Loading…</Box>
                ) : (
                  history.map((h) => (
                    <Box key={`${h.ts}-${h.actor}`}>
                      <Box sx={{ color: c.ink, fontWeight: 600 }}>
                        {h.actor}{h.actorKind === "agent" ? " (agent)" : ""} · {whenLabel(h.ts)}
                      </Box>
                      {h.changes.map((ch) => (
                        <Box key={ch.field} sx={{ display: "flex", gap: 1, fontFamily: brainTokens.font.mono, fontSize: 11 }}>
                          <Box sx={{ minWidth: 96 }}>{HISTORY_FIELD_LABELS[ch.field] ?? ch.field}</Box>
                          <Box>{ch.from ?? "—"} → {ch.to ?? "—"}</Box>
                        </Box>
                      ))}
                    </Box>
                  ))
                )}
                {/* Synthesised anchor — creates don't write a history row (design §4.3). */}
                <Box>
                  Created{edit.createdAt ? ` · ${new Date(edit.createdAt).toLocaleDateString("en-NZ")}` : ""}
                  {edit.raisedBy ? ` · raised by ${edit.raisedBy}` : ""}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEdit(null)}>Cancel</Button>
          <Button variant="contained" disabled={busy || !edit?.text?.trim()} onClick={() => void save()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
